import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { useIntl } from 'react-intl';
import withSizes from 'react-sizes';
import { ThemeContext } from 'styled-components';
import { Tooltip, TooltipSelect, TooltipContainer } from '@luigiclaudio/ga-baseline-ui/tooltip';
import { Button } from '@luigiclaudio/ga-baseline-ui/button';
import { isSizes } from '@luigiclaudio/ga-baseline-ui/layout';
import ColorToolsContext from '../../context/ColorToolsContext';
import CustomColorPicker from '../../CustomColorPicker';
import Container from './Container';

const TooltipColorPicker = ({ show, name, isMobile, isLargeMobile }) => {
    const intl = useIntl();
    const { values, setFieldValue } = useFormikContext();
    const theme = useContext(ThemeContext);
    const { setColorOnStage, setColorMixedOnStage } = useContext(ColorToolsContext);

    const placement = isMobile ? 'bottom' : 'bottom start';

    const renderPickerWidth = useMemo(() => {
        const pickerDefaultWidth = theme.site.colorPicker.width;

        if (isMobile) {
            return pickerDefaultWidth;
        }
        if (isLargeMobile) {
            return pickerDefaultWidth * 1.25;
        }
        return pickerDefaultWidth * 1.5;
    }, [theme, isLargeMobile, isMobile]);

    const handleOnChange = (color, fieldName) => {
        setFieldValue(name, color.hex.slice(1));

        if (fieldName === 'color') {
            setColorOnStage(color.hex.slice(1));
        }
        if (fieldName === 'mixedColor') {
            setColorMixedOnStage(color.hex.slice(1));
        }
    };

    if (!show) {
        return null;
    }

    return (
        <Container>
            <Tooltip placement={placement} centerPointer maxWidth={0}>
                <TooltipSelect>
                    <Button
                        icon="droplet"
                        size="md"
                        skin="muted"
                        btnType="outlined"
                        title={intl.formatMessage({ id: 'color.colorPicker.button.title' })}
                    />
                </TooltipSelect>
                <TooltipContainer>
                    <CustomColorPicker
                        color={values[name]}
                        onChange={(color) => {
                            handleOnChange(color, name);
                        }}
                        pickerWidth={renderPickerWidth}
                    />
                </TooltipContainer>
            </Tooltip>
        </Container>
    );
};

const mapSizesToProps = ({ width }, { theme }) => {
    return {
        isLargeMobile: isSizes.smView({ width }, { theme }),
        isMobile: isSizes.xsView({ width }, { theme }),
        isXxs: isSizes.xxsView({ width }, { theme }),
    };
};

TooltipColorPicker.propTypes = {
    show: PropTypes.bool,
    name: PropTypes.string.isRequired,
    isMobile: PropTypes.bool,
    isLargeMobile: PropTypes.bool,
};

TooltipColorPicker.defaultProps = {
    show: true,
    isMobile: false,
    isLargeMobile: false,
};

export default withSizes(mapSizesToProps)(TooltipColorPicker);
